/**
 * ApprovalsRoute — `/godkjenninger`, the standalone approval queue.
 *
 * This route stays mounted after the parent console landed, and deliberately
 * so: every chore-completed push and both SLA escalations deep-link to
 * `/godkjenninger?id=<instanceId>` (see chores.subscribers + the
 * approvalSlaNudge job), so notifications already delivered to family phones
 * point here. Removing it would break links we cannot recall.
 *
 * The route owns the session gate and the page padding; <ApprovalsPanel> owns
 * the queue and its header (#155: Godkjenn er en hovedmenyfane, og konsollens
 * Godkjenning-fane er borte). Skjermen tegner sin egen topp med avataren, så
 * ruten står på OWN_HEADER_ROUTES i navTabs.ts.
 *
 * Auth gate (D-15 / T-02-08):
 *   • requireAuth via /api/auth/me; if role !== 'admin' → 403 fallback
 *     "Bare voksne kan godkjenne — be om hjelp".
 *   • The server is the authoritative gate on approve/reject
 *     (requireAdminScope); this client gate is UX.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApprovalsPanel } from './ApprovalsPanel';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';

interface SessionMember {
  id: string;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
  birth_date?: string | null;
  color_token: ColorSlot;
}

function useSession(): { viewer: SessionMember | null; loaded: boolean } {
  const [viewer, setViewer] = useState<SessionMember | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', { credentials: 'same-origin' });
        if (!r.ok) {
          if (!cancelled) setLoaded(true);
          return;
        }
        const body = (await r.json()) as { member?: SessionMember };
        if (!cancelled) {
          setViewer(body.member ?? null);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { viewer, loaded };
}

interface ApprovalsRouteProps {
  /** Override clock for tests. */
  now?: Date;
}

export function ApprovalsRoute({ now }: ApprovalsRouteProps = {}): ReactNode {
  const { t } = useTranslation();
  const { viewer, loaded } = useSession();
  const isAdmin = viewer?.role === 'admin';

  // While we wait for /me, render a tiny loading placeholder.
  if (!loaded) {
    return (
      <main data-testid="approvals-loading" className="min-h-dvh p-8">
        <p className="text-ink-2 text-sm">{t('approvals.loading')}</p>
      </main>
    );
  }

  // Non-admin → 403 fallback (T-02-08 client-side gate).
  if (!isAdmin) {
    return (
      <main
        data-testid="approvals-403"
        className="flex min-h-dvh items-center justify-center p-8"
      >
        <p className="text-ink-2 text-sm">{t('approvals.notAdminFallback')}</p>
      </main>
    );
  }

  return (
    <main data-testid="approvals-route" className="min-h-dvh px-5 pt-4 pb-6 md:px-8">
      <ApprovalsPanel isAdmin {...(now ? { now } : {})} />
    </main>
  );
}
